import { forwardRef, useRef, useImperativeHandle } from 'react';
import useModalState from '@sdk/components/ui/modal/useModalState';
import Modal from '@sdk/components/ui/modal/Modal';
import gettext from '@sdk/lang/gettext';
import H3 from '@sdk/components/ui/typography/H3';

import type {
  RestangularEmployee,
  RestangularPosition,
} from '@web/onboarding/Checklist/DefineWagesModal/defineWagesModal.types';
import type { UpdateWages } from '@web/onboarding/Checklist/onboardingChecklist.types';
import { Container, CenterContentRow } from '@web/onboarding/Checklist/components/modalBase.style';
import NoEmployeesNoPositionsContent from '@web/onboarding/Checklist/DefineWagesModal/NoEmployeesNoPositionsContent';
import WagesForEmployeesContentWithData from '@web/onboarding/Checklist/DefineWagesModal/WagesForEmployeesContentWithData';
import WagesForPositionsContentWithData from '@web/onboarding/Checklist/DefineWagesModal/WagesForPositionsContentWithData';
import WagesForEmployeesAndPositionsContent from '@web/onboarding/Checklist/DefineWagesModal/WagesForEmployeesAndPositionsContent';

type ForwardedAttrs = {
  open: () => void;
};

type Props = {
  employees: RestangularEmployee[];
  positions: RestangularPosition[];
  openAddEmployeesStep: () => void;
  updateWages: UpdateWages;
};

const DefineWagesModal = forwardRef<ForwardedAttrs, Props>(
  ({ employees, positions, openAddEmployeesStep, updateWages }, ref) => {
    const { isVisible, open, close } = useModalState();
    const config = useRef({ noDismiss: true }).current;

    // If org has 1 employee that means it only has org creator = no employees
    const orgHasEmployees = employees.length > 1;
    const orgHasPositions = Boolean(positions.length);

    useImperativeHandle(ref, () => ({
      open,
    }));

    if (!isVisible) {
      return null;
    }

    const onAddEmployees = () => {
      close();
      openAddEmployeesStep();
    };

    return (
      <Modal fixed config={config} isVisible={isVisible} onDismiss={close}>
        {!orgHasEmployees && !orgHasPositions && (
          <NoEmployeesNoPositionsContent onAddEmployees={onAddEmployees} />
        )}
        {orgHasEmployees && !orgHasPositions && (
          <Container>
            <CenterContentRow pTop={20} pBottom={20}>
              <H3>{gettext('Add Wages')}</H3>
            </CenterContentRow>
            <WagesForEmployeesContentWithData
              employees={employees}
              onCancel={close}
              afterSubmitCallback={close}
              updateWages={updateWages}
            />
          </Container>
        )}
        {!orgHasEmployees && orgHasPositions && (
          <Container>
            <CenterContentRow pTop={20} pBottom={20}>
              <H3>{gettext('Add Wages')}</H3>
            </CenterContentRow>
            <WagesForPositionsContentWithData
              positions={positions}
              onCancel={close}
              afterSubmitCallback={close}
              updateWages={updateWages}
            />
          </Container>
        )}
        {orgHasEmployees && orgHasPositions && (
          <WagesForEmployeesAndPositionsContent
            employees={employees}
            positions={positions}
            onCancel={close}
            afterSubmitCallback={close}
            updateWages={updateWages}
          />
        )}
      </Modal>
    );
  }
);

DefineWagesModal.displayName = 'DefineWagesModal';

export default DefineWagesModal;
